import { Podcast, Episode } from '../types';

const DB_NAME = 'PodcastDB';
const DB_VERSION = 1;
const PODCASTS_STORE = 'podcasts';
const EPISODES_STORE = 'episodes';

class PodcastDB {
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('IndexedDB failed to open:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('IndexedDB initialized successfully');
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        console.log('IndexedDB upgrade needed, creating stores...');

        if (!db.objectStoreNames.contains(PODCASTS_STORE)) {
          db.createObjectStore(PODCASTS_STORE, { keyPath: 'id' });
          console.log('Created podcasts store');
        }

        if (!db.objectStoreNames.contains(EPISODES_STORE)) {
          const episodesStore = db.createObjectStore(EPISODES_STORE, { keyPath: 'id' });
          episodesStore.createIndex('podcastId', 'podcastId', { unique: false });
          console.log('Created episodes store');
        }
      };
    });
  }

  async addPodcast(podcast: Podcast): Promise<void> {
    if (!this.db) await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([PODCASTS_STORE], 'readwrite');
      const store = transaction.objectStore(PODCASTS_STORE);
      const request = store.add(podcast);

      request.onsuccess = () => {
        console.log('Podcast saved to IndexedDB:', podcast.title);
        resolve();
      };
      request.onerror = () => {
        console.error('Failed to save podcast:', request.error);
        reject(request.error);
      };
    });
  }

  async getPodcasts(): Promise<Podcast[]> {
    if (!this.db) await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([PODCASTS_STORE], 'readonly');
      const store = transaction.objectStore(PODCASTS_STORE);
      const request = store.getAll();

      request.onsuccess = () => {
        console.log('Loaded podcasts from IndexedDB:', request.result.length);
        resolve(request.result);
      };
      request.onerror = () => {
        console.error('Failed to load podcasts:', request.error);
        reject(request.error);
      };
    });
  }

  async deletePodcast(id: string): Promise<void> {
    if (!this.db) await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([PODCASTS_STORE], 'readwrite');
      const store = transaction.objectStore(PODCASTS_STORE);
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async addEpisodes(episodes: Episode[]): Promise<void> {
    if (!this.db) await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([EPISODES_STORE], 'readwrite');
      const store = transaction.objectStore(EPISODES_STORE);

      episodes.forEach((episode) => {
        store.put(episode);
      });

      transaction.oncomplete = () => {
        console.log('Saved episodes to IndexedDB:', episodes.length);
        resolve();
      };
      transaction.onerror = () => {
        console.error('Failed to save episodes:', transaction.error);
        reject(transaction.error);
      };
    });
  }

  async getEpisodes(): Promise<Episode[]> {
    if (!this.db) await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([EPISODES_STORE], 'readonly');
      const store = transaction.objectStore(EPISODES_STORE);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getEpisodesByPodcast(podcastId: string): Promise<Episode[]> {
    if (!this.db) await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([EPISODES_STORE], 'readonly');
      const store = transaction.objectStore(EPISODES_STORE);
      const index = store.index('podcastId');
      const request = index.getAll(podcastId);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async updateEpisode(episode: Episode): Promise<void> {
    if (!this.db) await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([EPISODES_STORE], 'readwrite');
      const store = transaction.objectStore(EPISODES_STORE);
      const request = store.put(episode);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }
}

export const db = new PodcastDB();

// Test function to verify storage is working
export async function testStorage(): Promise<void> {
  try {
    await db.init();
    const podcasts = await db.getPodcasts();
    const episodes = await db.getEpisodes();
    console.log('📊 Storage Status:');
    console.log(`  - Podcasts: ${podcasts.length}`);
    console.log(`  - Episodes: ${episodes.length}`);
    console.log(`  - Storage: ${podcasts.length > 0 ? '✅ Working' : '⚠️ Empty (add a podcast to test)'}`);
  } catch (error) {
    console.error('❌ Storage test failed:', error);
  }
}
